import { useMemo, useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { CodeBlock } from '@components/code-block';
import { FormLabel } from '@components/form-label';
import { HideContainer } from '@components/hide-container';
import { IconAiAgent } from '@components/icons/ai-agent';
import { IconFieldCustom } from '@components/icons/field-custom';
import { IconOutput } from '@components/icons/output';
import { CloseButton, ContentSeparator, SimpleIconHeader } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { TextArea } from '@components/textarea';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { asteriskLabel } from '@utils/strings';

import { createLabelFromKey, extractUrlPlaceholders, unwrapRoot } from './list-utils';

import {
  useJqValidation,
  useValidateListTransformJq,
  useGenerateListTransformJq,
} from '../integrations-hooks';
import type { SyncListParameter } from '../integrations-types';

export type EditSyncListTransformPanelProps = {
  exampleData: object | null;
  exampleDataWrapped: boolean;
  initialJq: string | null;
  onSave: (jq: string, parameterTestValues: Record<string, string>) => void;
  onClose: () => void;
  parameters: SyncListParameter[];
  allowDynamicList: boolean;
  connectionVariableNames?: string[];
};

export const EditSyncListTransformPanel = ({
  exampleData,
  exampleDataWrapped,
  initialJq,
  onSave,
  onClose,
  parameters,
  allowDynamicList,
  connectionVariableNames,
}: EditSyncListTransformPanelProps) => {
  const [aiPrompt, setAiPrompt] = useState('');
  const [jqQuery, setJqQuery] = useState(initialJq ?? '');
  const [initialTestValues] = useState(() =>
    Object.fromEntries(parameters.map(({ name, testValue }) => [name, testValue])),
  );
  const [testValues, setTestValues] = useState(initialTestValues);
  const { t } = useScopedTranslate(locale);

  const displayData = exampleData ? unwrapRoot(exampleData, exampleDataWrapped) : null;

  const validateMutation = useValidateListTransformJq();
  const generateMutation = useGenerateListTransformJq();
  const parameterNames = useMemo(
    () =>
      allowDynamicList
        ? extractUrlPlaceholders(jqQuery).filter((name) => !connectionVariableNames?.includes(name))
        : [],
    [allowDynamicList, connectionVariableNames, jqQuery],
  );
  const variables = useMemo(
    () => Object.fromEntries(parameterNames.map((name) => [name, testValues[name] ?? ''])),
    [parameterNames, testValues],
  );
  const { isValidationPending } = useJqValidation(
    validateMutation,
    jqQuery,
    displayData,
    variables,
  );

  const handleGenerateQuery = () => {
    if (!aiPrompt || !displayData) {
      return;
    }
    generateMutation.mutate(
      { prompt: aiPrompt, exampleData: displayData, variables: testValues },
      {
        onSuccess: (result) => {
          if (result.success && result.query) {
            setJqQuery(result.query);
          }
        },
      },
    );
  };

  const changedTestValues = Object.fromEntries(
    Object.entries(variables).filter(([name, value]) => value !== (initialTestValues[name] ?? '')),
  );

  const handleSave = () => {
    onSave(jqQuery, changedTestValues);
    onClose();
  };

  const hasValidationError = Boolean(validateMutation.data?.error);
  const isDirty = jqQuery !== (initialJq ?? '') || Object.keys(changedTestValues).length > 0;
  const queryResult = validateMutation.data?.result;
  // The transform has to produce a list/object of options — a scalar or no
  // output would fail at sync time.
  const isResultValidShape = typeof queryResult === 'object' && queryResult !== null;
  const isSaveDisabled =
    !jqQuery ||
    !isDirty ||
    isValidationPending ||
    hasValidationError ||
    !isResultValidShape ||
    generateMutation.isLoading;

  const isValidationComplete = Boolean(jqQuery) && !isValidationPending;
  const validationSucceeded = isValidationComplete && Boolean(validateMutation.data?.success);
  const showEmptyState = !jqQuery;
  const showValidating = Boolean(jqQuery) && isValidationPending;
  const showRequestError = isValidationComplete && validateMutation.isError;
  const showQueryError = isValidationComplete && hasValidationError;
  const showEmptyResult = validationSucceeded && queryResult === null;
  const showInvalidShape = validationSucceeded && queryResult !== null && !isResultValidShape;
  const showResult = validationSucceeded && isResultValidShape;

  return (
    <PanelLayout>
      <StickyHeader>
        <Stack space={4}>
          <Stack space={2} orientation="horizontal" align="center" justify="space-between">
            <SimpleIconHeader
              icon={IconFieldCustom}
              iconColor="elementBlue100"
              title={t(initialJq ? 'editCustomOptions' : 'addCustomOptions')}
            />
            <CloseButton onClose={onClose} />
          </Stack>
          <ContentSeparator />
        </Stack>
      </StickyHeader>
      <ScrollContent>
        <Stack space={6}>
          <HideContainer title={t('endpointData')}>
            {displayData ? (
              <CodeBlock data={displayData} hideFooter />
            ) : (
              <Stack space={1}>
                <Text text={t('noEndpointData')} font="semibold14" color="orange100" />
                <Text text={t('testEndpointFirst')} font="regular14" color="textSecondary" />
              </Stack>
            )}
          </HideContainer>

          <ContentSeparator />

          <HideContainer title={t('configuration')}>
            <Stack space={4}>
              {displayData && (
                <Stack space={2}>
                  <Text text={t('aiPrompt')} font="semibold14" aiGradient />
                  <TextArea
                    rows={2}
                    value={aiPrompt}
                    onChange={setAiPrompt}
                    placeholder={t('aiPromptPlaceholder')}
                    isDisabled={validateMutation.isLoading || generateMutation.isLoading}
                  />
                  <div>
                    <Button
                      text={generateMutation.isLoading ? t('generating') : t('generateJqQuery')}
                      variant="ai-gradient"
                      iconLeft={<IconAiAgent variant="gradient" />}
                      onClick={handleGenerateQuery}
                      isDisabled={
                        !aiPrompt || generateMutation.isLoading || validateMutation.isLoading
                      }
                    />
                  </div>
                  {!generateMutation.isLoading && generateMutation.data?.error && (
                    <Text text={generateMutation.data.error} font="regular14" color="red100" />
                  )}
                </Stack>
              )}

              <Stack space={1}>
                <FormLabel text={asteriskLabel(t('core.jqQuery'))} />
                <TextArea
                  rows={3}
                  value={jqQuery}
                  onChange={setJqQuery}
                  isDisabled={generateMutation.isLoading}
                />
                <Text
                  text={allowDynamicList ? t('queryInfoWithParameters') : t('queryInfo')}
                  font="regular14"
                  color="textSecondary"
                />
              </Stack>

              {parameterNames.length > 0 && (
                <Stack space={2}>
                  <Text text={t('parameters')} font="semibold14" />
                  <Text text={t('parametersInfo')} font="regular14" color="textSecondary" />
                  {parameterNames.map((name) => (
                    <TextField
                      key={name}
                      label={
                        parameters.find((parameter) => parameter.name === name)?.label ??
                        createLabelFromKey(name)
                      }
                      value={testValues[name] ?? ''}
                      onChange={(value) =>
                        setTestValues((current) => ({ ...current, [name]: value }))
                      }
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </HideContainer>

          <ContentSeparator />

          <HideContainer title={t('result')}>
            {showEmptyState && (
              <Card space={6} variant="gray">
                <Stack space={4} orientation="horizontal" align="center">
                  <IconOutput color="textSecondary" />
                  <Text text={t('addQueryForOutput')} font="semibold14" color="textSecondary" />
                </Stack>
              </Card>
            )}

            <div role="status" aria-live="polite">
              {showValidating && (
                <Text text={t('validating')} font="regular14" color="textSecondary" />
              )}
              {showRequestError && (
                <Text text={t('validationFailed')} font="regular14" color="red100" />
              )}
              {showQueryError && (
                <Text text={validateMutation.data?.error ?? ''} font="regular14" color="red100" />
              )}
              {showEmptyResult && <Text text={t('resultEmpty')} font="regular14" color="red100" />}
              {showInvalidShape && (
                <Text text={t('resultMustBeList')} font="regular14" color="red100" />
              )}
              {showResult && <CodeBlock data={queryResult} hideFooter />}
            </div>
          </HideContainer>
        </Stack>
      </ScrollContent>
      <PanelFooter>
        <Stack orientation="horizontal" space={2} justify="flex-end">
          <Button text={t('core.cancel')} variant="secondary" onClick={onClose} />
          <Button
            text={t('core.save')}
            variant="primary-blue"
            onClick={handleSave}
            isDisabled={isSaveDisabled}
          />
        </Stack>
      </PanelFooter>
    </PanelLayout>
  );
};

const PanelLayout = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  padding: `${pxToRem(16)} ${pxToRem(24)}`,
});

const StickyHeader = styled.div({
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: theme.color.backgroundLightDark,
});

const ScrollContent = styled.div({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  paddingTop: pxToRem(24),
  paddingBottom: pxToRem(40),
});

// Bleed to the container edges so the top border spans full width.
const PanelFooter = styled.div({
  margin: `0 ${pxToRem(-24)}`,
  padding: `${pxToRem(16)} ${pxToRem(24)} 0`,
  borderTop: `${pxToRem(1)} solid ${theme.color.border10}`,
  backgroundColor: theme.color.backgroundLightDark,
});

const locale = {
  en: {
    addCustomOptions: 'Add custom options',
    editCustomOptions: 'Edit custom options',
    endpointData: 'Endpoint data',
    configuration: 'Configuration',
    result: 'Result',
    noEndpointData: 'No endpoint data available',
    testEndpointFirst:
      'Test the endpoint first to generate example data. The jq query will run against the test response.',
    queryInfo:
      'The jq query will be executed against the API response when the integration runs. Refer to https://jqlang.github.io/jq/ for syntax.',
    queryInfoWithParameters:
      'The jq query will be executed against the API response when the integration runs. Write {{name}} to insert a parameter value; it is inserted as a string. Refer to https://jqlang.github.io/jq/ for syntax.',
    parameters: 'Parameters',
    parametersInfo: 'Used to run the result below.',
    addQueryForOutput: 'Add or generate jq query to get output',
    validating: 'Validating query...',
    validationFailed: 'Could not validate the query. Please try again.',
    resultEmpty: 'The jq query returned no data.',
    resultMustBeList: 'The query must return a list of options, not a single value.',
    aiPrompt: 'AI Prompt',
    aiPromptPlaceholder: 'Describe what you want to select and have AI structure it for you',
    generateJqQuery: 'Generate jq query',
    generating: 'Generating...',
  },
};
